import { useCallback, useMemo, useState } from 'react';
import { ApiError } from '@/api/errors';
import { serverMessage } from '@/api/serverMessage';
import { minorUnitFactor } from '@/utils/v2Format';

const OVERRIDE_ERROR_COPY: Readonly<Record<string, string>> = {
  OVERRIDE_NOT_SUPPORTED_ON_LINE: "This line's price cannot be changed.",
  OVERRIDE_NOT_ALLOWED_ON_COMP: 'A comped line cannot also carry a price.',
  OVERRIDE_AMOUNT_INVALID: 'The amount is not a valid price.',
  CHARGE_OUT_OF_BOUNDS:
    'The negotiated price is outside the allowed range. A PIN is required, or the one given was not accepted.',
  OVERRIDE_PIN_INVALID: 'That PIN was not accepted.',
  OVERRIDE_LOCKED:
    'Too many incorrect PINs. Negotiated prices are temporarily locked. Try again in 15 minutes.',
};

export interface NegotiatedPriceErrorView {
  message: string;
  retryable: boolean;
}

/**
 * The shared DNKADM-0334/0335 PIN state machine. Draft keystrokes never become
 * request authorization; only the explicit Apply action snapshots the exact value.
 */
export function useNegotiatedPriceOverride(active: boolean) {
  const [draftPin, setDraftPinState] = useState('');
  const [committedPin, setCommittedPin] = useState<string | null>(null);

  const overrideAuth = useMemo(
    () => (active && committedPin !== null ? { pin: committedPin } : undefined),
    [active, committedPin],
  );

  const setDraftPin = useCallback((value: string) => {
    setDraftPinState(value);
    setCommittedPin(null);
  }, []);

  const apply = useCallback(() => {
    if (!active || draftPin.trim().length === 0) return;
    // Preserve the operator's exact entry. Correctness and format belong to pay-api.
    setCommittedPin(draftPin);
  }, [active, draftPin]);

  const invalidate = useCallback(() => setCommittedPin(null), []);
  const reset = useCallback(() => {
    setDraftPinState('');
    setCommittedPin(null);
  }, []);

  return { draftPin, overrideAuth, setDraftPin, apply, invalidate, reset };
}

/**
 * Convert major-unit operator input to the contract's integer minor units. This
 * checks only wire representability; amount bands remain exclusively server-owned.
 */
export function negotiatedPriceToMinor(value: string, currency: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  const scaled = parsed * minorUnitFactor(currency);
  const rounded = Math.round(scaled);
  return Math.abs(scaled - rounded) < 1e-7 && Number.isSafeInteger(rounded) && rounded > 0
    ? rounded
    : null;
}

export function majorPriceInput(value: number): string {
  return Number.isFinite(value) ? String(value) : '';
}

/** A canonical ISO-4217 code supported by this runtime, or null when unavailable. */
export function canonicalCurrencyCode(value: string | null): string | null {
  if (value === null) return null;
  const code = value.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return null;
  try {
    return Intl.supportedValuesOf('currency').includes(code) ? code : null;
  } catch {
    return null;
  }
}

/** Shared frozen six-code mapping, including the server-message scrub seam. */
export function negotiatedPriceErrorView(error: unknown): NegotiatedPriceErrorView | null {
  if (!(error instanceof ApiError)) return null;
  const code = apiEnvelopeCode(error);
  const copy = code === null ? undefined : OVERRIDE_ERROR_COPY[code];
  if (copy === undefined) return null;
  const message = serverMessage(error);
  const suffix = error.requestId ? ` Request ID: ${error.requestId}` : '';
  return {
    retryable: code === 'OVERRIDE_LOCKED' && error.retryable === true,
    message: `${copy}${message ? ` ${message}` : ''}${suffix}`,
  };
}

function apiEnvelopeCode(error: ApiError): string | null {
  const code = error.details?.['code'];
  return typeof code === 'string' ? code : null;
}
